/**
 * ⚠️ AUTO-GENERATED FILE — DO NOT EDIT
 *
 * Source: server/modules/shared/roros-enums.ts
 * Generator: scripts/generate-roros-enums.mjs
 *
 * To update: edit the server source, then run `npm run codegen:enums`.
 */
// ─── Domains ─────────────────────────────────────────────────────────────────
// Integer IDs are business identifiers for roros_domains.domain_id.
// All FK references use the UUID roros_domains.id column.
export enum RorosDomain {
  STRATEGY = 1,
  MARKETING = 2,
  SALES = 3,
  PRODUCT = 4,
  ENGINEERING = 5,
  CUSTOMER = 6,
  FINANCE_LEGAL = 7,
  PEOPLE = 8,
  ADMINISTRATION = 9,
  QUALITY = 10,
  OPERATIONAL_EXCELLENCE = 11,
}

// ─── Operating Modes ─────────────────────────────────────────────────────────
export enum RorosOperatingMode {
  FOUNDER = 'founder',
  GROWTH = 'growth',
  SCALING = 'scaling',
}

// ─── Actor Types ─────────────────────────────────────────────────────────────
/**
 * Types of participants in the system. An "Actor" is any participant
 * that can perform work or be the subject of work.
 *
 * `HUMAN` is the only actor type that is also a "Member" — a human
 * with login credentials whose `roros_actors.user_id` is non-null and
 * references `auth_users`. All other types are non-Member actors.
 *
 * Use the Actor concept when assigning, attributing, or checking
 * participation for actions that may be performed by humans or AI.
 * Use the Member concept (assert `actor_type === RorosActorType.HUMAN`)
 * only when the action must be performed by a human — e.g., approvals,
 * quality-review sign-offs, explicit confirmations of responsibility.
 *
 * See PRD §Glossary for the Actor vs. Member distinction and
 * examples of where it matters.
 */
export enum RorosActorType {
  HUMAN = 'human',
  AI_OPERATOR = 'ai_operator',
  KNOWLEDGE_PERSONA = 'knowledge_persona',
}

// ─── Adoption Levels ─────────────────────────────────────────────────────────
export enum RorosAdoptionLevel {
  SHADOW = 'shadow',
  COPILOT = 'copilot',
  AUTOPILOT = 'autopilot',
}

// ─── Work Item Types ─────────────────────────────────────────────────────────
export enum RorosWorkItemType {
  TASK = 'task',
  DELIVERABLE = 'deliverable',
  IMPROVEMENT_ITEM = 'improvement_item',
  INTAKE_ITEM = 'intake_item',
}

// ─── Work Item Statuses ──────────────────────────────────────────────────────
export enum RorosWorkItemStatus {
  DRAFT = 'draft',
  BACKLOG = 'backlog',
  READY = 'ready',
  IN_PROGRESS = 'in_progress',
  DONE = 'done',
  QUALITY_CONFIRMATION = 'quality_confirmation',
  RELEASED = 'released',
  BLOCKED = 'blocked',
  ESCALATED = 'escalated',
}

// ─── Priority ────────────────────────────────────────────────────────────────
// Integer mapping: 1 = Critical, 2 = High, 3 = Medium (default), 4 = Low, 5 = Lowest
export const RorosPriority = {
  CRITICAL: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
  LOWEST: 5,
} as const;
export type RorosPriority = typeof RorosPriority[keyof typeof RorosPriority];

// ─── Artifact Types ──────────────────────────────────────────────────────────
export const RorosArtifactType = {
  OUTCOME: 'outcome',
  METRIC: 'metric',
  PRINCIPLE: 'principle',
  ROUTINE: 'routine',
  INITIATIVE: 'initiative',
  KNOWLEDGE: 'knowledge',
  LEARNING: 'learning',
} as const;
export type RorosArtifactType = typeof RorosArtifactType[keyof typeof RorosArtifactType];

// ─── Decision Impact ─────────────────────────────────────────────────────────
export enum RorosDecisionImpact {
  ROOT = 'root',
  TRUNK = 'trunk',
  BRANCH = 'branch',
  LEAF = 'leaf',
}

// ─── Decision Methods ────────────────────────────────────────────────────────
export enum RorosDecisionMethod {
  AUTOCRATIC = 'autocratic',
  CONSULTATIVE = 'consultative',
  CONSENT_BASED = 'consent_based',
  CONSENSUS = 'consensus',
  DELEGATION = 'delegation',
  VOTING = 'voting',
  DELIBERATIVE_ALIGNMENT = 'deliberative_alignment',
}

// ─── Cadence Types ───────────────────────────────────────────────────────────
export enum RorosCadenceType {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  TRIMESTER = 'trimester',
  ANNUAL = 'annual',
}

// ─── Knowledge Item Types ────────────────────────────────────────────────────
export enum RorosKnowledgeItemType {
  DOCUMENT = 'document',
  FACT = 'fact',
  TEMPLATE = 'template',
  CHECKLIST = 'checklist',
  STANDARD = 'standard',
  DECISION_LOG = 'decision_log',
  METHOD_LIBRARY = 'method_library',
  ADVISORY_BOARD = 'advisory_board',
  SOURCE_OF_TRUTH = 'source_of_truth',
}

// ─── Visibility Modes ────────────────────────────────────────────────────────
export const RorosVisibilityMode = {
  OPEN: 'open',
  DOMAIN_SCOPED: 'domain_scoped',
  RESTRICTED: 'restricted',
  CUSTOM: 'custom',
} as const;
export type RorosVisibilityMode = typeof RorosVisibilityMode[keyof typeof RorosVisibilityMode];

// ─── PDCA States ─────────────────────────────────────────────────────────────
export enum RorosPdcaState {
  PLAN = 'plan',
  DO = 'do',
  CHECK = 'check',
  ACT = 'act',
  COMPLETED = 'completed',
  ESCALATED = 'escalated',
}

// ─── Initiative Statuses ─────────────────────────────────────────────────────
export enum RorosInitiativeStatus {
  PLANNED = 'planned',
  IN_PROGRESS = 'in_progress',
  DONE = 'done',
  STOPPED = 'stopped',
  CONVERTED_TO_ROUTINE = 'converted_to_routine',
  ARCHIVED = 'archived',
}

// ─── Learning Item Statuses ──────────────────────────────────────────────────
export enum RorosLearningStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  APPLIED = 'applied',
  REJECTED = 'rejected',
  ARCHIVED = 'archived',
}

// ─── Role Contract Statuses ──────────────────────────────────────────────────
export enum RorosRoleContractStatus {
  TEMPLATE = 'template',
  DRAFT = 'draft',
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

// ─── Decision Note Statuses ──────────────────────────────────────────────────
export enum RorosDecisionNoteStatus {
  DRAFT = 'draft',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  DEFERRED = 'deferred',
  ARCHIVED = 'archived',
}

// ─── Cadence Review Statuses ─────────────────────────────────────────────────
export enum RorosCadenceReviewStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
}

// ─── Exploration Zone Statuses ───────────────────────────────────────────────
export enum RorosExplorationZoneStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  REVOKED = 'revoked',
}

// ─── Task Delegation Statuses ────────────────────────────────────────────────
export enum RorosTaskDelegationStatus {
  CREATED = 'created',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

// ─── Learning Source Tags ────────────────────────────────────────────────────
export enum RorosLearningSourceTag {
  ROUTINE_RUN = 'routine_run',
  CADENCE_REVIEW = 'cadence_review',
  EXTERNAL_OBSERVATION = 'external_observation',
}

// ─── Maturity Ladder Stages ──────────────────────────────────────────────────
export enum RorosMaturityStage {
  AD_HOC = 1,
  GOVERNED = 2,
  VERIFIED = 3,
  COMPOUNDING = 4,
}

// ─── QC Report Types ─────────────────────────────────────────────────────────
export enum RorosQcReportType {
  FULL = 'full',
  LITE = 'lite',
}

// ─── State Machine Definitions ───────────────────────────────────────────────

export const WORK_ITEM_TRANSITIONS: Record<string, string[]> = {
  // BF-M2-03 FIX: 'draft' was in RorosWorkItemStatus enum but absent from the
  // transition matrix, causing the state-machine coverage test to fail.
  // draft → backlog is the standard flow (drafts are not yet committed).
  // draft cannot be reached from any other status (entry point only).
  draft: ['backlog'],
  backlog: ['ready'],
  ready: ['in_progress', 'backlog'],
  in_progress: ['done', 'blocked', 'escalated'],
  done: ['quality_confirmation', 'escalated'],
  quality_confirmation: ['released', 'in_progress'],
  released: [],
  blocked: ['in_progress', 'escalated'],
  escalated: ['in_progress', 'blocked'],
};

export const INITIATIVE_TRANSITIONS: Record<string, string[]> = {
  planned: ['in_progress'],
  in_progress: ['done', 'stopped'],
  done: ['converted_to_routine', 'archived'],
  stopped: ['archived'],
  converted_to_routine: ['archived'],
  archived: [],
};

export const DECISION_NOTE_TRANSITIONS: Record<string, string[]> = {
  draft: ['pending_approval'],
  pending_approval: ['approved', 'rejected', 'deferred'],
  approved: ['archived'],
  rejected: ['archived'],
  deferred: ['draft', 'archived'],
  archived: [],
};

export const LEARNING_ITEM_TRANSITIONS: Record<string, string[]> = {
  open: ['in_progress', 'rejected'],
  in_progress: ['applied', 'rejected'],
  applied: ['archived'],
  rejected: ['archived'],
  archived: [],
};

export const PDCA_TRANSITIONS: Record<string, string[]> = {
  plan: ['do'],
  do: ['check'],
  check: ['act', 'do'],
  act: ['completed', 'escalated'],
  completed: [],
  escalated: [],
};

export const CADENCE_REVIEW_TRANSITIONS: Record<string, string[]> = {
  scheduled: ['in_progress', 'skipped'],
  in_progress: ['completed'],
  completed: [],
  skipped: [],
};

export const ROLE_CONTRACT_TRANSITIONS: Record<string, string[]> = {
  template: [],
  draft: ['active'],
  active: ['archived'],
  archived: [],
};
